import { change } from 'redux-form';
import _ from 'lodash';
import shortid from 'shortid';

import { loading, success, handleErrorException } from 'State/actions';
import { ImportIngredientsFieldEnum as Fields } from 'Common/FieldEnum';
import { IMPORT_INGREDIENT_FORM_NAME } from 'Common/FormEnum';
import { generateHeaderList } from 'Common/Components/ImportPreview/helpers';
import { SKIP } from 'Common/Components/ImportPreview/helpers/Constants';

import * as Selectors from './selectors';
import * as types from './types';

export const getImportIngColumns = () => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, true));

    try {
      const res = await MPWWebApi.getGeneric(
        'ImportIngredients/GetImportIngredientColumns'
      );
      const data = res.data.Data;

      dispatch(success(types.IMPORT_INGREDIENT_SET_INGREDIENT_COLUMNS, data));
      dispatch(
        change(
          IMPORT_INGREDIENT_FORM_NAME,
          Fields.SelectedFieldList,
          generateHeaderList(data, data.length)
        )
      );
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.IMPORT_INGREDIENT_FETCH_ERROR, {
          Title: 'Error',
          Message: 'Failed to load ingredient fields to map to.',
        })
      );
    } finally {
      dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, false));
    }
  };
};

export const getSavedImportConfigs = () => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, true));

    try {
      const res = await MPWWebApi.getGeneric(
        'ImportIngredients/GetImportIngredientConfig'
      );
      const data = res.data.Data;
      dispatch(success(types.IMPORT_INGREDIENT_SET_SAVED_IMPORT_CONFIGS, data));
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.IMPORT_INGREDIENT_FETCH_ERROR, {
          Title: 'Error',
          Message: 'Failed to load import ingredient configuration.',
        })
      );
    } finally {
      dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, false));
    }
  };
};

export const importIngredients = data => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, true));
    const state = getState();

    try {
      const fieldConfig = _.pickBy(data, (value, key) => {
        return key !== Fields.File;
      });
      const formData = new FormData();
      formData.append(Fields.File, data[Fields.File]);
      formData.append(
        Fields.FieldConfig,
        JSON.stringify({
          ...fieldConfig,
          [Fields.SelectedFieldList]: Selectors.getSelectedFieldListForAPI(
            state
          ),
        })
      );
      await MPWWebApi.postGenericFile(
        'ImportIngredients/WriteImportIngredients',
        formData,
        dispatch
      );
      return true;
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.IMPORT_INGREDIENT_FETCH_ERROR, {
          Title: 'Error',
          Message: 'Failed to import ingredents.',
        })
      );
    } finally {
      dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, false));
    }
    return false;
  };
};

export const importIngredientsAndSaveConfig = data => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, true));
  const state = getState();

  try {
    const file = data[Fields.File];
    const filePath = file.name;
    const fieldConfig = _.pickBy(data, (value, key) => {
      return key !== Fields.File;
    });
    const importConfig = {
      ...fieldConfig,
      [Fields.SelectedFieldList]: Selectors.getSelectedFieldListForAPI(state),
      [Fields.FilePath]: filePath,
    };
    const savedImportConfigs = Selectors.getSavedImportConfigs(state);

    const formData = new FormData();
    formData.append(Fields.File, data[Fields.File]);
    formData.append(Fields.FieldConfig, JSON.stringify(importConfig));
    await MPWWebApi.postGenericInsertUpdate(
      'ImportIngredients/WriteImportIngredientConfig',
      importConfig
    );

    await MPWWebApi.postGenericFile(
      'ImportIngredients/WriteImportIngredients',
      formData,
      dispatch
    );

    dispatch({
      type: types.IMPORT_INGREDIENT_SET_SAVED_IMPORT_CONFIGS,
      payload: [
        ...savedImportConfigs,
        { ...importConfig, Id: shortid.generate() },
      ],
    });

    return true;
  } catch (ex) {
    dispatch(
      handleErrorException(ex, types.IMPORT_INGREDIENT_FETCH_ERROR, {
        Title: 'Error',
        Message: 'Failed to import ingredents and save configurations.',
      })
    );
  } finally {
    dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, false));
  }
  return false;
};

export const clearError = () => ({
  type: types.IMPORT_INGREDIENT_CLEAR_ERROR,
});

export const updateSelectedFieldListForUploadedFile = () => (
  dispatch,
  getState
) => {
  const state = getState();
  const defaultSelectedFieldList = Selectors.getDefaultSelectedFieldList(state);
  const longestLineLength = Selectors.getLongestFileLineLength(state);
  const selectedFieldList = Selectors.getSelectedFieldList(state);
  const fieldListLength = selectedFieldList.length;

  let formSelectedFieldList = [];

  if (longestLineLength < fieldListLength) {
    // Shrink the user's current field list to match number of columns
    formSelectedFieldList = selectedFieldList.slice(0, longestLineLength);
  } else {
    formSelectedFieldList = defaultSelectedFieldList.slice(
      0,
      longestLineLength
    );

    // Preserve the user's current field list
    formSelectedFieldList.forEach((field, i) => {
      if (i < fieldListLength) {
        formSelectedFieldList[i] = selectedFieldList[i];
      } else {
        formSelectedFieldList[i] = {
          [Fields.ColumnLabel]: SKIP,
          [Fields.ColumnNumber]: i,
          FieldName: SKIP,
        };
      }
    });
  }

  dispatch(
    change(
      IMPORT_INGREDIENT_FORM_NAME,
      Fields.SelectedFieldList,
      formSelectedFieldList
    )
  );
};

export const previewImportIngredient = file => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, true));

    try {
      const state = getState();
      const delimiter = Selectors.getDelimiter(state);
      const formData = new FormData();
      formData.append('import_file', file);
      formData.append('delimiter', delimiter);

      const res = await MPWWebApi.postGenericFile('import', formData, dispatch);
      const data = res.data.Data;

      dispatch(success(types.IMPORT_INGREDIENT_SET_PREVIEW_DATA, data));
      dispatch(updateSelectedFieldListForUploadedFile());
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.IMPORT_INGREDIENT_FETCH_ERROR, {
          Title: 'Error',
          Message: 'Failed to load preview for ingredient import.',
        })
      );
    } finally {
      dispatch(loading(types.IMPORT_INGREDIENT_FETCHING, false));
    }
  };
};

export const clearFileAndPreview = () => dispatch => {
  dispatch(change(IMPORT_INGREDIENT_FORM_NAME, Fields.File, null));
  dispatch(success(types.IMPORT_INGREDIENT_SET_PREVIEW_DATA, []));
};

export const clearColumnMapping = () => (dispatch, getState) => {
  const state = getState();
  const ingredientColumns = Selectors.getIngredientColumns(state);
  const clearedFields = [];

  for (let i = 0; i < ingredientColumns.length; i++) {
    clearedFields.push({
      [Fields.ColumnLabel]: SKIP,
      [Fields.ColumnNumber]: i,
      FieldName: SKIP,
    });
  }

  dispatch(
    change(IMPORT_INGREDIENT_FORM_NAME, Fields.SelectedFieldList, clearedFields)
  );
};

export const resetColumnMapping = () => (dispatch, getState) => {
  const state = getState();
  const shouldResetToSavedConfig = Selectors.shouldLoadSavedConfig(state);
  const longestLineLength = Selectors.getLongestFileLineLength(state);
  const defaultSelectedFieldList = Selectors.getDefaultSelectedFieldList(
    state
  ).slice(0, longestLineLength);
  const ingredientColumns = Selectors.getIngredientColumns(state);

  if (shouldResetToSavedConfig) {
    const savedConfigsMap = Selectors.getSavedImportConfigsMap(state);
    const loadedConfigId = Selectors.getLoadedConfigId(state);
    const savedConfig = savedConfigsMap[loadedConfigId];

    if (!savedConfig) {
      dispatch(
        change(
          IMPORT_INGREDIENT_FORM_NAME,
          Fields.SelectedFieldList,
          defaultSelectedFieldList
        )
      );
    } else {
      const selectedFieldList = savedConfig[Fields.SelectedFieldList];
      dispatch(
        change(
          IMPORT_INGREDIENT_FORM_NAME,
          Fields.SelectedFieldList,
          generateHeaderList(selectedFieldList, ingredientColumns.length).slice(
            0,
            longestLineLength
          )
        )
      );
    }
  } else {
    dispatch(
      change(
        IMPORT_INGREDIENT_FORM_NAME,
        Fields.SelectedFieldList,
        defaultSelectedFieldList
      )
    );
  }
};

export const resetState = () => ({
  type: types.IMPORT_INGREDIENT_RESET_STATE,
});



// WEBPACK FOOTER //
// ./src/pages/importingredients/state/actions.js